import { divisionSchoolOptions, pubSportsObject, pubStateOptions } from '../config/configExtras';
import { updateProfile } from '../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { isBookingProcessAlias } from '../transactions/transaction';
import { BOOKING } from './constants';
import { UserType } from './enums';
import { isFieldForCategory, isFieldForListingType, pickCategoryFields } from './fieldHelpers';
import { isArrayLength } from './genericHelpers';
import {
  DEFAULT_BOOKING_PROCESS,
  DEFAULT_BOOKING_UNIT_TYPE,
  EXTENDED_DATA_SCHEMA_TYPES,
  SCHEMA_TYPE_ENUM,
} from './types';
import { getUserDetails, getUserName, getUserType } from './userDataExtraction';
import { uploadImages } from '../containers/EditListingPage/EditListingPage.duck';
import moment from 'moment';
import { getMarketplaceEntities } from '../ducks/marketplaceData.duck';

const setImageData = (key, index, images, updatedValues) => {
  if (index !== null && index !== undefined) {
    updatedValues.publicData[key] = {
      index,
      imageId: images[index]?.uuid,
    };
  }
};
// Helper function to process array fields
export const processArrayField = array => {
  return isArrayLength(array) ? array.map(item => item.value) : [];
};
export const processAffiliationField = array => {
  return isArrayLength(array) ? array.map(item => item.organization) : [];
};
export const getListingTitle = (
  organizationName,
  agencyName,
  pub_school_or_group_name,
  firstName,
  lastName,
  userType
) => {
  // Mapping of user types to their respective title fields
  const titleMap = {
    brand: organizationName,
    agency: agencyName,
    school: pub_school_or_group_name,
    talent: firstName && lastName ? `${firstName} ${lastName}` : null,
  };

  // Validate userType
  if (
    !userType ||
    typeof userType !== 'string' ||
    !titleMap.hasOwnProperty(userType.toLowerCase())
  ) {
    return 'Unnamed';
  }

  // Get the title value for the given userType
  const titleValue = titleMap[userType.toLowerCase()];

  // Return trimmed title or 'Unnamed' if invalid
  return typeof titleValue === 'string' && titleValue.trim().length > 0
    ? titleValue.trim()
    : 'Unnamed';
};

// Helper function to prepare listing values
export const prepareListingValues = ({ firstName, lastName, userType, listingParams, images }) => {
  const {
    location = {},
    pictures,
    logo,
    profilePicture,
    listingImageConfig,
    profileImageIndex,
    tradingCardImageIndex,
    pub_agency,
    favoriteProTeams = [],
    favoriteCollegiateTeams = [],
    pub_sports = [],
    pub_interest = [],
    description,
    pub_school_or_group_name,
    agencyName,
    organizationName,
    dateOfBirth,
    ...rest
  } = listingParams || {};
  const formattedDOB = moment(dateOfBirth).format('M/DD/YYYY');
  const formattedAge = moment().diff(dateOfBirth, 'years');
  const followersCount = calculateTotalFollowers(listingParams?.profileDetails);

  // Safely destructure `address` and `origin` with full null checks
  const selectedPlace = location?.selectedPlace || {};
  const address = selectedPlace?.address || '';
  const origin = selectedPlace?.origin || null;
  const title = getListingTitle(
    organizationName,
    agencyName,
    pub_school_or_group_name,
    firstName,
    lastName,
    listingParams.userType
  );
  const updatedValues = {
    title,
    ...(description && { description }),
    publicData: {
      ...(followersCount && { totalFollowers: followersCount }),
      ...(dateOfBirth && {
        dateOfBirth: formattedDOB,
        age: formattedAge,
      }),
      ...(userType === UserType.SCHOOL && { schoolDisplayName: `${firstName}${lastName}` }),
      transactionProcessAlias: DEFAULT_BOOKING_PROCESS,
      unitType: DEFAULT_BOOKING_UNIT_TYPE,
      listingType: BOOKING,
      listingCategory: userType,
      ...(pub_agency && {
        pub_agency: {
          name: pub_agency.value?.trim(),
          id: pub_agency.id,
        },
        agency_name: pub_agency.value?.trim(),
      }),
      // Process array fields consistently
      ...(favoriteProTeams.length > 0 && {
        favoriteProTeams: processArrayField(favoriteProTeams),
      }),
      ...(favoriteCollegiateTeams.length > 0 && {
        favoriteCollegiateTeams: processArrayField(favoriteCollegiateTeams),
      }),
      ...(pub_sports.length > 0 && {
        pub_sports: processArrayField(pub_sports),
      }),
      ...(pub_interest.length > 0 && {
        pub_interest: processArrayField(pub_interest),
      }),
      ...rest,
    },
  };

  // Only set geolocation and location if both origin and address are available
  if (origin && address) {
    updatedValues.geolocation = origin;
    updatedValues.publicData.location = { address };
  }

  if (isArrayLength(images)) {
    updatedValues.images = images;
  }
  setImageData('tradingCardImage', tradingCardImageIndex, images, updatedValues);
  setImageData('profileImage', profileImageIndex, images, updatedValues);

  return updatedValues;
};

export const insertCustomFields = fieldArray => {
  // Find the indices of the pub_position and pub_hometown objects
  const positionIndex = fieldArray.findIndex(field => field.key === 'pub_position');
  const hometownIndex = fieldArray.findIndex(field => field.key === 'pub_hometown');

  // Insert the sports object at the position index
  if (positionIndex !== -1) {
    fieldArray.splice(positionIndex, 0, pubSportsObject);
  }

  // Insert the state object next to the hometown object
  if (hometownIndex !== -1) {
    fieldArray.splice(hometownIndex + 1, 0, pubStateOptions);
  }

  return fieldArray;
};

/**
 * Get listing configuration. For existing listings, it is stored to publicData.
 * For new listings, the data needs to be figured out from listingTypes configuration.
 *
 * In the latter case, we select first type in the array. However, EditListingDetailsForm component
 * gets 'selectableListingTypes' prop, which it uses to provide a way to make selection,
 * if multiple listing types are available.
 *
 * @param {Array} listingTypes
 * @param {Object} existingListingTypeInfo
 * @returns an object containing mainly information that can be stored to publicData.
 */
export const getTransactionInfo = (
  listingTypes,
  existingListingTypeInfo = {},
  inlcudeLabel = false
) => {
  const { listingType, transactionProcessAlias, unitType } = existingListingTypeInfo;

  if (listingType && transactionProcessAlias && unitType) {
    return { listingType, transactionProcessAlias, unitType };
  } else if (listingTypes.length === 1) {
    const { listingType: type, label, transactionType } = listingTypes[0];
    const { alias, unitType: configUnitType } = transactionType;
    const labelMaybe = inlcudeLabel ? { label: label || type } : {};
    return {
      listingType: type,
      transactionProcessAlias: alias,
      unitType: configUnitType,
      ...labelMaybe,
    };
  }
  return {};
};

/**
 * Check if listingType has already been set.
 *
 * If listing type (incl. process & unitType) has been set, we won't allow change to it.
 * It's possible to make it editable, but it becomes somewhat complex to modify following panels,
 * for the different process. (E.g. adjusting stock vs booking availability settings,
 * if process has been changed for existing listing.)
 *
 * @param {Object} publicData JSON-like data stored to listing entity.
 * @returns object literal with to keys: { hasExistingListingType, existingListingTypeInfo }
 */
export const hasSetListingType = publicData => {
  const { listingType, transactionProcessAlias, unitType, listingCategory } = publicData;
  const existingListingTypeInfo = {
    listingType,
    transactionProcessAlias,
    unitType,
    listingCategory,
  };

  return {
    hasExistingListingType: !!listingType && !!transactionProcessAlias && !!unitType,
    existingListingTypeInfo,
  };
};

/**
 * Pick extended data fields from given form data.
 * Picking is based on extended data configuration for the listing and target scope and listing type.
 *
 * This expects submit data to be namespaced (e.g. 'pub_') and it returns the field without that namespace.
 * This function is used when form submit values are restructured for the actual API endpoint.
 *
 * Note: This returns null for those fields that are managed by configuration, but don't match target listing type.
 *       These might exists if provider swaps between listing types before saving the draft listing.
 *
 * @param {Object} data values to look through against listingConfig.js and util/configHelpers.js
 * @param {String} targetScope Check that the scope of extended data the config matches
 * @param {String} targetListingType Check that the extended data is relevant for this listing type.
 * @param {Object} listingFieldConfigs an extended data configurtions for listing fields.
 * @returns Array of picked extended data fields from submitted data.
 */
export const pickListingFieldsData = (
  data,
  targetScope,
  targetListingType,
  targetCategories,
  listingFieldConfigs
) => {
  const targetCategoryIds = Object.values(targetCategories);

  return listingFieldConfigs.reduce((fields, fieldConfig) => {
    const { key, scope = 'public', schemaType } = fieldConfig || {};
    const namespacePrefix = scope === 'public' ? `pub_` : `priv_`;
    const namespacedKey = `${namespacePrefix}${key}`;

    const isKnownSchemaType = EXTENDED_DATA_SCHEMA_TYPES.includes(schemaType);
    const isTargetScope = scope === targetScope;
    const isTargetListingType = isFieldForListingType(targetListingType, fieldConfig);
    const isTargetCategory = isFieldForCategory(targetCategoryIds, fieldConfig);

    if (isKnownSchemaType && isTargetScope && isTargetListingType && isTargetCategory) {
      const fieldValue = data[namespacedKey] || null;
      return { ...fields, [key]: fieldValue };
    } else if (isKnownSchemaType && isTargetScope) {
      // Note: this clears extra custom fields
      // These might exists if provider swaps between listing types before saving the draft listing.
      return { ...fields, [key]: null };
    }
    return fields;
  }, {});
};

/**
 * Pick extended data fields from given extended data of the listing entity.
 * Picking is based on extended data configuration for the listing and target scope and listing type.
 *
 * This returns namespaced (e.g. 'pub_') initial values for the form.
 *
 * @param {Object} data extended data values to look through against listingConfig.js and util/configHelpers.js
 * @param {String} targetScope Check that the scope of extended data the config matches
 * @param {String} targetListingType Check that the extended data is relevant for this listing type.
 * @param {Object} listingFieldConfigs an extended data configurtions for listing fields.
 * @returns Array of picked extended data fields
 */
export const initialValuesForListingFields = (
  data,
  targetScope,
  targetListingType,
  targetCategories,
  listingFieldConfigs
) => {
  const targetCategoryIds = Object.values(targetCategories);

  return listingFieldConfigs.reduce((fields, fieldConfig) => {
    const { key, scope = 'public', schemaType, enumOptions } = fieldConfig || {};
    const namespacePrefix = scope === 'public' ? `pub_` : `priv_`;
    const namespacedKey = `${namespacePrefix}${key}`;

    const isKnownSchemaType = EXTENDED_DATA_SCHEMA_TYPES.includes(schemaType);
    const isEnumSchemaType = schemaType === SCHEMA_TYPE_ENUM;
    const shouldHaveValidEnumOptions =
      !isEnumSchemaType ||
      (isEnumSchemaType && !!enumOptions?.find(conf => conf.option === data?.[key]));
    const isTargetScope = scope === targetScope;
    const isTargetListingType = isFieldForListingType(targetListingType, fieldConfig);
    const isTargetCategory = isFieldForCategory(targetCategoryIds, fieldConfig);

    if (
      isKnownSchemaType &&
      isTargetScope &&
      isTargetListingType &&
      isTargetCategory &&
      shouldHaveValidEnumOptions
    ) {
      const fieldValue = data?.[key] || null;
      return { ...fields, [namespacedKey]: fieldValue };
    }
    return fields;
  }, {});
};

/**
 * If listing represents something else than a bookable listing, we set availability-plan to seats=0.
 * Note: this is a performance improvement since the API is backwards compatible.
 *
 * @param {string} processAlias selected for this listing
 * @returns availabilityPlan without any seats available for the listing
 */
export const setNoAvailabilityForUnbookableListings = processAlias => {
  return isBookingProcessAlias(processAlias)
    ? {}
    : {
        availabilityPlan: {
          type: 'availability-plan/time',
          timezone: 'Etc/UTC',
          entries: [
            // Note: "no entries" is the same as seats=0 for every entry.
            // { dayOfWeek: 'mon', startTime: '00:00', endTime: '00:00', seats: 0 },
            // { dayOfWeek: 'tue', startTime: '00:00', endTime: '00:00', seats: 0 },
            // { dayOfWeek: 'wed', startTime: '00:00', endTime: '00:00', seats: 0 },
            // { dayOfWeek: 'thu', startTime: '00:00', endTime: '00:00', seats: 0 },
            // { dayOfWeek: 'fri', startTime: '00:00', endTime: '00:00', seats: 0 },
            // { dayOfWeek: 'sat', startTime: '00:00', endTime: '00:00', seats: 0 },
            // { dayOfWeek: 'sun', startTime: '00:00', endTime: '00:00', seats: 0 },
          ],
        },
      };
};

export const getListingImage = images => {
  if (!images?.length) return null;

  return images.map(image => ({
    fileLink:
      image.attributes?.variants?.['scaled-medium']?.url ||
      image.attributes?.variants?.['scaled-medium-2x']?.url,
    id: image.id,
  }));
};

const getUserImages = (userType, images) => {
  let logo = null,
    pictures = null;
  let fanProfilePicture = null;
  switch (userType) {
    case UserType.SCHOOL:
    case UserType.AGENCY:
    case UserType.BRAND:
      logo = getListingImage(images);
      break;
    case UserType.FAN:
      fanProfilePicture = getListingImage(images);
      break;
    case UserType.TALENT:
      pictures = getListingImage(images);
      break;
    default:
      break;
  }
  return { logo, pictures, fanProfilePicture };
};
const getLocationInitialValues = listing => {
  const { geolocation, publicData } = listing?.attributes || {};

  // Only render current search if full place object is available in the URL params
  // TODO bounds are missing - those need to be queried directly from Google Places
  const locationFieldsPresent = publicData?.location?.address && geolocation;
  const location = publicData?.location || {};
  const { address } = location;

  return {
    location: locationFieldsPresent
      ? {
          search: address,
          selectedPlace: { address, origin: geolocation },
        }
      : null,
  };
};

// Helper function to transform array values to value-label objects
const transformArrayToValueLabel = arr => {
  return isArrayLength(arr)
    ? arr.map(item => ({
        value: item,
        label: item?.charAt(0).toUpperCase() + item?.slice(1),
      }))
    : [];
};
/**
 * Get initialValues for the form. This function includes
 * title, description, listingType, transactionProcessAlias, unitType,
 * and those publicData & privateData fields that are configured through
 * config.listing.listingFields.
 *
 * @param {object} props
 * @param {object} existingListingTypeInfo info saved to listing's publicData
 * @param {object} listingTypes app's configured types (presets for listings)
 * @param {object} listingFields those extended data fields that are part of configurations
 * @returns initialValues object for the form
 */
export const getInitialValues = (
  props,
  existingListingTypeInfo,
  listingTypes,
  listingFields,
  listingCategories,
  categoryKey,
  currentUser,
  userFields
) => {
  // Extract listing and its attributes with safe fallbacks
  const listing = props?.listing || {};
  const { description = '', title = '', publicData = {}, privateData = {}, price = null } =
    listing?.attributes || {};
  // Extract specific fields from public data with destructuring
  const {
    listingType,
    listingCategory,
    location,
    socialMedia,
    pub_agency,
    bannerImage,
    favoriteProTeams = [],
    favoriteCollegiateTeams = [],
    pub_sports = [],
    pub_interest = [],
    ...restPublicData
  } = publicData;

  // Retrieve user details and images
  const userType = getUserType(currentUser);
  const username = getUserName(currentUser);
  const { dateOfBirth, ...userDetails } = getUserDetails(currentUser);
  const nestedCategories = pickCategoryFields(restPublicData, categoryKey, 1, listingCategories);
  const { logo, pictures, fanProfilePicture } = getUserImages(userType, listing?.images || []);
  // Format agency data
  const updatedAgency = pub_agency
    ? { value: pub_agency?.name, label: pub_agency?.name, id: pub_agency?.id }
    : null;

  const titleMapping = {
    [UserType.SCHOOL]: 'pub_school_or_group_name',
    [UserType.AGENCY]: 'agencyName',
    [UserType.BRAND]: 'organizationName',
  };
  const mappedTitleKey = titleMapping[userType];
  const test = { [mappedTitleKey]: title };
  // Build initial values object
  const initialValues = {
    [mappedTitleKey]: title,
    price,
    dateOfBirth: dateOfBirth ? moment(dateOfBirth) : null,
    logo,
    pictures,
    profilePicture: fanProfilePicture,
    userType,
    username,
    title,
    bannerImage,
    social_media: socialMedia || null,
    pub_agency: updatedAgency,
    description,
    ...getLocationInitialValues(listing),
    ...userDetails,
    ...restPublicData,
    ...privateData,
    ...getTransactionInfo(listingTypes, existingListingTypeInfo),
  };

  // Add array fields with value-label format only if they exist
  if (favoriteProTeams.length > 0) {
    initialValues.favoriteProTeams = transformArrayToValueLabel(favoriteProTeams);
  }

  if (favoriteCollegiateTeams.length > 0) {
    initialValues.favoriteCollegiateTeams = transformArrayToValueLabel(favoriteCollegiateTeams);
  }

  if (pub_sports.length > 0) {
    initialValues.pub_sports = transformArrayToValueLabel(pub_sports);
  }

  if (pub_interest.length > 0) {
    initialValues.pub_interest = transformArrayToValueLabel(pub_interest);
  }

  // Add listing field values
  return {
    ...initialValues,
    ...initialValuesForListingFields(
      publicData,
      'public',
      listingType,
      nestedCategories,
      listingFields
    ),
    ...initialValuesForListingFields(
      privateData,
      'private',
      listingType,
      nestedCategories,
      listingFields
    ),
  };
};
// Function to upload images and return uploaded images
export const handleImageUploads = async ({
  pictures = [], // Default to empty array
  logo = [], // Default to empty array
  dispatch,
  listingImageConfig,
  isAuthPage,
  profileImageIndex = 0, // Default index
}) => {
  try {
    // Normalize logo to always be an array
    const logoArray = Array.isArray(logo) ? logo : logo ? [logo] : [];
    const picturesArray = Array.isArray(pictures) ? pictures : pictures ? [pictures] : [];

    // Ensure arrays are always defined with spread operator
    const imageUploads = [...picturesArray, ...logoArray];

    // Safely select profile picture with fallback
    const profilePicture =
      picturesArray.length > 0
        ? picturesArray[profileImageIndex]
        : logoArray.length > 0
        ? logoArray[0]
        : null;

    // Upload all images
    const images = await dispatch(uploadImages(imageUploads, listingImageConfig, isAuthPage));
    // Find the index of profilePicture in imageUploads
    const profileIdx = profilePicture ? imageUploads.indexOf(profilePicture) : -1;
    // Filter local uploads (those without id.uuid)
    const localUploads = imageUploads.filter(file => !file.id?.uuid);

    // Upload only local images
    const uploadedResponses =
      localUploads.length > 0
        ? await dispatch(uploadImages(localUploads, listingImageConfig, isAuthPage))
        : [];

    // Build updatedImages preserving order: use existing id for stored, new id for local
    let updatedImages = [];
    let localPtr = 0;
    for (const file of imageUploads) {
      if (file.id?.uuid) {
        // If the file is already stored (has UUID), just use its existing ID—no upload needed
        updatedImages.push({ imageId: file.id });
      } else {
        // For local files, grab the corresponding upload response (in order of localUploads)
        const resp = uploadedResponses[localPtr++];
        const imageId = resp?.id || resp?.imageId || resp?.data?.id;
        if (imageId) {
          // Push the new ID from the upload response
          updatedImages.push({ imageId });
        }
      }
    }
    updatedImages = updatedImages.filter(Boolean); // Remove any null/undefined entries (e.g., failed uploads)

    // Prioritize profile image: move it to the first position
    if (profileIdx >= 0 && profileIdx < updatedImages.length) {
      const profileImg = updatedImages[profileIdx];
      if (profileImg) {
        // Remove the profile image from its current position
        updatedImages.splice(profileIdx, 1);
        // Insert it at the beginning (index 0) to prioritize it
        updatedImages.unshift(profileImg);
      }
    }

    // Upload profile picture if it exists
    const uploadedProfilePicture = profilePicture
      ? await dispatch(uploadImages([profilePicture], listingImageConfig, isAuthPage))
      : null;

    return {
      images: isAuthPage ? images : updatedImages,
      uploadedProfilePicture,
    };
  } catch (error) {
    throw error;
  }
};
// Function to update user profile
export const updateUserProfile = ({
  uploadedProfilePicture,
  firstName,
  lastName,
  phoneNumber,
  dateOfBirth,
  dispatch,
  username,
}) => {
  // Create the profile update object
  const profileUpdate = {
    firstName,
    lastName,
    protectedData: {
      dateOfBirth: moment(dateOfBirth).format('LL'),
      phoneNumber,
    },
    publicData: {
      ...(username && { username }),
    },
  };

  // Add profileImageId only if uploadedProfilePicture is not null and has a valid length
  if (isArrayLength(uploadedProfilePicture)) {
    profileUpdate.profileImageId = uploadedProfilePicture[0]?.imageId?.uuid;
  }

  // Dispatch the profile update
  dispatch(updateProfile(profileUpdate));
};

// Memoized helper functions
export const getConferences = division => {
  const conferences = divisionSchoolOptions
    .filter(school => school.division === division)
    .map(school => school.conference);
  return [...new Set(conferences)];
};

export const getSchools = (division, conference) => {
  return divisionSchoolOptions.filter(
    school => school.division === division && school.conference === conference
  );
};
/**
 * Calculates the total number of followers from an array of profile details
 * and returns the total as a number.
 * @param {Array} profileDetails - The array of profile detail objects.
 * @returns {number} The total number of followers.
 */
export const calculateTotalFollowers = profileDetails => {
  if (!profileDetails || !profileDetails.length) return 0;

  return profileDetails.reduce((acc, item) => {
    // Only include items that are of type 'socialMedia' and have a followers field
    if (item.type === 'socialMedia' && item.followers) {
      // Remove commas from the string, then parse into a number
      const numericFollowers = parseInt(item.followers.replace(/,/g, ''), 10);
      return acc + (isNaN(numericFollowers) ? 0 : numericFollowers);
    }
    return acc;
  }, 0);
};
export const getOwnListing = (state, id) => {
  const ref = { id, type: 'ownListing' };
  const listings = getMarketplaceEntities(state, [ref]);
  return listings.length === 1 ? listings[0] : null;
};
